import { useState, useEffect } from 'react';
import { getAudienceSocket } from '../hooks/useAudienceState';
import NicknameBar from '../components/NicknameBar';
import { useCountdown } from '../hooks/useCountdown';

interface BalanceChallengeScreenProps {
  challengeId: string;
  config: {
    targetScore: number;
    balanceMargin?: number;
  };
  endsAt: number;
  serverTime: number;
  initialScore: number;
  targetScore: number;
  initialWhiteClicks?: number;
  initialBlackClicks?: number;
}

function BalanceChallengeScreen({
  challengeId,
  endsAt,
  serverTime,
  initialScore,
  targetScore,
}: BalanceChallengeScreenProps) {
  const remainingSeconds = useCountdown(endsAt, serverTime);
  const [score, setScore] = useState(initialScore);
  const [pressedButton, setPressedButton] = useState<'white' | 'black' | null>(null);

  const progressPercent = Math.min(100, Math.round((score / targetScore) * 100));

  // Ascolta tick dal server per aggiornamento score
  useEffect(() => {
    const socket = getAudienceSocket();
    if (!socket) return;

    const handleTick = (data: { challengeId: string; score: number }) => {
      if (data.challengeId === challengeId) {
        setScore(data.score);
      }
    };

    socket.on('challenge:tick', handleTick);
    return () => { socket.off('challenge:tick', handleTick); };
  }, [challengeId]);

  // Gestisci click su bottone
  const handleClick = (color: 'white' | 'black') => {
    const socket = getAudienceSocket();
    if (!socket) return;

    socket.emit('challenge:tap', { challengeId, variant: 'balance', color });

    // Feedback locale
    setPressedButton(color);
    setTimeout(() => setPressedButton(null), 100);
  };

  return (
    <div
      className="flex flex-col min-h-screen select-none"
      style={{ touchAction: 'manipulation' }}
    >
      <NicknameBar />

      {/* Header: countdown + progress */}
      <div className="pt-12 px-4 pb-2">
        <div className="flex items-center justify-between mb-2">
          <span className={`text-2xl font-heading font-bold ${remainingSeconds <= 5 ? 'text-team-red animate-pulse' : 'text-ink'}`}>
            {remainingSeconds}s
          </span>
          <span className="text-ink-light text-base font-heading">{score}/{targetScore}</span>
        </div>
        {/* Progress bar */}
        <div className="progress-parchment">
          <div
            className="h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Istruzioni */}
      <div className="text-center px-4 py-3">
        <p className="text-ink-light text-base font-body">Premi un bottone! Riempi la barra mantenendo l'equilibrio</p>
      </div>

      {/* Due bottoni grandi */}
      <div className="flex-1 flex items-center justify-center gap-6 px-6 pb-8">
        {/* Bottone Rosso (white) */}
        <button
          onTouchStart={(e) => { e.preventDefault(); handleClick('white'); }}
          onMouseDown={() => handleClick('white')}
          className={`flex-1 aspect-square max-w-[160px] rounded-full border-4 border-red-400/40 transition-transform duration-100 ${
            pressedButton === 'white' ? 'scale-90' : 'scale-100 active:scale-90'
          }`}
          style={{
            background: 'radial-gradient(circle at 35% 35%, #f87171, #b91c1c)',
            boxShadow: '0 0 30px rgba(239, 68, 68, 0.4), inset 0 -4px 8px rgba(0, 0, 0, 0.2)',
          }}
        />

        {/* Bottone Verde (black) */}
        <button
          onTouchStart={(e) => { e.preventDefault(); handleClick('black'); }}
          onMouseDown={() => handleClick('black')}
          className={`flex-1 aspect-square max-w-[160px] rounded-full border-4 border-green-400/40 transition-transform duration-100 ${
            pressedButton === 'black' ? 'scale-90' : 'scale-100 active:scale-90'
          }`}
          style={{
            background: 'radial-gradient(circle at 35% 35%, #4ade80, #15803d)',
            boxShadow: '0 0 30px rgba(74, 222, 128, 0.4), inset 0 -4px 8px rgba(0, 0, 0, 0.2)',
          }}
        />
      </div>
    </div>
  );
}

export default BalanceChallengeScreen;
